import { useState, useEffect, useCallback } from 'react';
import { urlConfig } from '../config';

const API_TIMEOUT_MS = 12000;

async function loadLocalGifts() {
  try {
    const base = process.env.PUBLIC_URL || '';
    const response = await fetch(`${base}/data/gifts.json`);
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

async function fetchRemoteGifts() {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), API_TIMEOUT_MS);
  try {
    const response = await fetch(`${urlConfig.backendUrl}/api/gifts`, {
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const data = await response.json();
    return Array.isArray(data) && data.length ? data : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

export function useGifts() {
  const [gifts, setGifts] = useState([]);
  const [ready, setReady] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const refreshFromApi = useCallback(async () => {
    setSyncing(true);
    const remote = await fetchRemoteGifts();
    if (remote) setGifts(remote);
    setSyncing(false);
    return remote;
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const local = await loadLocalGifts();
      if (!cancelled) {
        if (local.length) setGifts(local);
        setReady(true);
      }
      if (!cancelled) await refreshFromApi();
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshFromApi]);

  return { gifts, setGifts, ready, syncing, refreshFromApi };
}

export default useGifts;
